<template>
  <div class="shipping-block-list-item" :class="{ active: address.default_shipping }" @click="setDefault">
    <address-card-information :address="address" />
    <div v-if="!isSimpleLayout" class="shipping-block-list-item-actions">
      <a class="shipping-block-list-item-actions-link no-underline" @click.stop="openEditForm" v-if="!isCheckout">
        {{ $t('Edit') }}
      </a>
      <a v-if="!address.default_shipping" class="shipping-block-list-item-actions-link no-underline" @click.stop="setDefault">
        {{ $t('Set main') }}
      </a>
      <span v-else class="shipping-block-list-item-actions-text"> {{ $t('Main address') }} </span>
    </div>
    <img v-if="!isSimpleLayout" @click.stop="removeAddress" src="../../../../../../assets/account/icon-trash.png"
         class="shipping-block-list-item-image" alt="trash" width="20" height="22" loading="eager" fetchpriority="high"
    >
  </div>
</template>

<script async>
import { mapActions, mapState } from 'vuex';
import i18n from '@vue-storefront/i18n';
import updateObject from '@vue-storefront/core/scripts/utils/update-object';
import checkoutInitialState from '@vue-storefront/core/modules/checkout/store/checkout/initial-state';

const AddressCardInformation = () => import(/* webpackChunkName: "vsf-address-card-information" */ 'theme/components/core/blocks/MyAccount/ShippingDetails/AddressList/AddressCardInformation.vue');

export default {
  name: 'AddressCard',
  components: {
    AddressCardInformation
  },
  props: {
    address: {
      type: Object,
      required: true
    },
    isSimpleLayout: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.user.current
    }),
    isCheckout () {
      return ['checkout', 'anywhere'].includes(this.$route.name)
    },
    isBroken () {
      return !this.address.longitude || !this.address.latitude || !this.address.api_details
    },
    checkoutShippingDetails () {
      return this.$store.state.checkout.shippingDetails
    }
  },
  methods: {
    ...mapActions({
      remove: 'address/delete',
      makeDefault: 'address/makeDefault',
      updateShippingDetailsState: 'shipping/updateShippingDetailsState',
      saveShippingDetails: 'checkout/saveShippingDetails'
    }),
    openEditForm () {
      if (this.isSimpleLayout || this.isCheckout) {
        this.$router.push({ name: 'my-shipping-details' }, () => {
          setTimeout(() => {
            this.$bus.$emit('customer-display-shipping-address-form', { type: 'edit', item: this.address })
          }, 100)
        })
        return
      }
      this.$bus.$emit('customer-display-shipping-address-form', { type: 'edit', item: this.address })
    },
    setDefault () {
      if (this.isBroken) {
        this.$bus.$emit('scroll-to-the-address-form');
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t('Address is not valid!'),
          action1: { label: i18n.t('OK') },
          action2: { label: i18n.t('Edit'), action: this.openEditForm }
        })
        return
      }
      if (!this.address.default_shipping) {
        this.makeDefault({ address_id: this.address.id })
          .then((res) => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'success',
              message: i18n.t('Your default address is updated!'),
              action1: { label: i18n.t('OK') }
            })
            this.$bus.$emit('checkout-after-shippingDetails', res);
            this.$bus.$emit('address-list-address-changed', res);
            this.updateShippingDetailsState({
              shipToMyAddress: true,
              showNewAddressForm: false
            })
          })
          .catch(() => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Error is happened during making address default!'),
              action1: { label: i18n.t('OK') }
            })
          })
      }
    },
    removeAddress () {
      this.remove({ address_id: this.address.id })
        .then(() => {
          if (this.address.default_shipping) {
            this.$bus.$emit('checkout-after-shippingDetails', updateObject(checkoutInitialState.shippingDetails, {
              firstName: this.checkoutShippingDetails.firstname,
              lastName: this.checkoutShippingDetails.lastname,
              phoneNumber: this.checkoutShippingDetails.telephone,
              shippingMethod: this.checkoutShippingDetails.shippingMethod,
              shippingCarrier: this.checkoutShippingDetails.shippingCarrier
            }), null);
          }
          this.$store.dispatch('notification/spawnNotification', {
            type: 'success',
            message: i18n.t('Your address is removed!'),
            action1: { label: i18n.t('OK') }
          })
        })
        .catch(() => {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: i18n.t('Error is happened during removing the address!'),
            action1: { label: i18n.t('OK') }
          })
        })
    }
  }
}
</script>
<style scoped lang="scss">
.shipping {
  &-block {
    &-list {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;

      &-item {
        text-align: left;
        min-height: 160px;
        max-width: calc(50% - 8px);
        flex: 1 1 calc(50% - 16px);

        display: flex;
        flex-direction: column;
        justify-content: space-between;

        position: relative;

        border: 1px solid #ebebeb;
        border-radius: 5px;

        @media only screen and (max-width: 991px) {
          flex: 1 1 100%;
          max-width: none;
        }
        &:hover {
          cursor: pointer;
          background: #F1F1F1;
        }

        &.active {
          border-color: #11bb2e;

          & .shipping-block-list-item-actions {
            justify-content: space-between;
          }

          & .shipping-block-list-item-actions-link:first-child:before {
            display: none;
          }
        }

        &-actions {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;
          min-height: 35px;

          &-link {
            cursor: pointer;
            margin-bottom: 8px;

            color: #11bb2e;
            font-size: 12px;

            &:first-child {
              position: relative;
              padding-left: 15px;
              padding-right: 14px;

              &:before {
                width: 1px;
                height: 13px;

                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;

                margin: auto;
                content: '';

                background: #ebebeb;
              }
            }

            &:last-child {
              padding-left: 14px;
            }

            &:hover {
              cursor: pointer;
              text-decoration: underline;
            }
          }

          &-text {
            padding: 8px 16px;

            color: #fff;
            font-size: 12px;
            font-weight: 600;

            border-top-left-radius: 5px;
            border-bottom-right-radius: 5px;

            background-color: #11bb2e;
          }
        }

        &-image {
          width: 18px;
          height: 20px;

          position: absolute;
          top: 10px;
          right: 10px;

          margin: auto;

          &:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
